import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  MessageSquare, 
  Mail, 
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  UserPlus,
  Package,
  DollarSign,
  Calendar,
  Users as UsersIcon,
  HelpCircle,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Dashboard from '../components/admin/Dashboard';
import Clients from '../components/admin/Clients';
import ClientDetails from '../components/admin/ClientDetails';
import Leads from '../components/admin/Leads';
import LeadDetails from '../components/admin/LeadDetails';
import Files from '../components/admin/Files';
import Messages from '../components/admin/Messages';
import SettingsPage from '../components/admin/Settings';
import ServicePackages from '../components/admin/ServicePackages';
import Invoicing from '../components/admin/Invoicing';
import TimeTracking from '../components/admin/TimeTracking';
import TaxDeadlines from '../components/admin/TaxDeadlines';
import TeamManagement from '../components/admin/TeamManagement';
import OnboardingTour from '../components/ui/OnboardingTour';
import { adminOnboardingSteps } from '../config/onboardingSteps';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if user has completed onboarding
  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem('admin_onboarding_completed');
    if (!hasCompletedOnboarding && location.pathname === '/admin/dashboard') {
      // Small delay to let the page render first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, [location.pathname]);

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Leads', href: '/admin/leads', icon: UserPlus },
    { name: 'Clients', href: '/admin/clients', icon: Users },
    { name: 'Files', href: '/admin/files', icon: FileText },
    { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
    { name: 'Services', href: '/admin/services', icon: Package },
    { name: 'Invoicing', href: '/admin/invoicing', icon: DollarSign },
    { name: 'Time Tracking', href: '/admin/time-tracking', icon: Clock },
    { name: 'Tax Deadlines', href: '/admin/tax-deadlines', icon: Calendar },
    { name: 'Team', href: '/admin/team', icon: UsersIcon },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center">
              <img 
                src="/ppmt-logo.png" 
                alt="PPMT Strategic Group" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-teal-700 font-semibold text-sm">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${active 
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg shadow-teal-500/30' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Help & Logout */}
          <div className="p-4 border-t border-gray-200 space-y-2">
            <Button
              data-testid="start-tour-button"
              onClick={() => setShowOnboarding(true)}
              variant="ghost"
              className="w-full justify-start text-teal-600 hover:text-teal-700 hover:bg-teal-50"
            >
              <HelpCircle className="w-5 h-5 mr-3" />
              Take a Tour
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700 mr-4"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {navigation.find(item => isActive(item.href))?.name || 'Admin Panel'}
          </h1>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard" />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:leadId" element={<LeadDetails />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:clientId" element={<ClientDetails />} />
            <Route path="/files" element={<Files />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/services" element={<ServicePackages />} />
            <Route path="/invoicing" element={<Invoicing />} />
            <Route path="/time-tracking" element={<TimeTracking />} />
            <Route path="/tax-deadlines" element={<TaxDeadlines />} />
            <Route path="/team" element={<TeamManagement />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>

      {/* Onboarding Tour */}
      <OnboardingTour
        steps={adminOnboardingSteps}
        isOpen={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
        storageKey="admin_onboarding_completed"
      />
    </div>
  );
};

export default AdminDashboard;
